import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../layout/PortalLayout";
import api from "../../api/client";
import { formatRelativeTime, NOTI_ICONS, resolveRoute } from "./notificationUtils";

const PAGE_SIZE = 20;

export default function NotificationsPage() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [page,          setPage]          = useState(1);
  const [hasMore,       setHasMore]       = useState(true);
  const [unreadCount,   setUnreadCount]   = useState(0);

  const listRef = useRef(null);

  const fetchPage = useCallback(async (pageNum, replace = false) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const res = await api.get(`portal-notifications/?page=${pageNum}&page_size=${PAGE_SIZE}`);
      const fetched = res.data.notifications ?? [];
      setNotifications(prev => replace ? fetched : [...prev, ...fetched.filter(n => !prev.find(p => p.id === n.id))]);
      setUnreadCount(res.data.unread_count ?? 0);
      setPage(pageNum);
      setHasMore(res.data.has_more ?? fetched.length >= PAGE_SIZE);
    } finally {
      if (pageNum === 1) setLoading(false); else setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  // Lazy scroll
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      fetchPage(page + 1);
    }
  }, [loadingMore, hasMore, page, fetchPage]);

  const markAllRead = async () => {
    await api.post("portal-notifications/read/", {});
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const markOneRead = async (id) => {
    await api.post("portal-notifications/read/", { ids: [id] });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleClick = (n) => {
    if (!n.is_read) markOneRead(n.id);
    const route = resolveRoute(n);
    if (route) navigate(route);
  };

  return (
    <PortalLayout>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              Notifications
              {unreadCount > 0 && (
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  background: "#ef4444", color: "white",
                  padding: "2px 8px", borderRadius: 20,
                }}>
                  {unreadCount} unread
                </span>
              )}
            </h1>
            <p className="page-subtitle">Your activity and pharmacy updates</p>
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead} style={{
              fontSize: 13, color: "var(--brand-primary)", fontWeight: 500,
              background: "var(--brand-light)", border: "1px solid var(--brand-accent)",
              borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="pi pi-check-square" style={{ fontSize: 12 }} />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="ph-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--text-muted)" }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
            <div style={{ fontSize: 13 }}>Loading notifications…</div>
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--text-muted)" }}>
            <i className="pi pi-bell" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No notifications yet</div>
            <div style={{ fontSize: 13 }}>Pharmacy updates and activity will appear here.</div>
          </div>
        ) : (
          <div ref={listRef} onScroll={handleScroll} style={{ overflowY: "auto", maxHeight: "calc(100dvh - 220px)" }}>
            {notifications.map((n, idx) => {
              const meta  = NOTI_ICONS[n.noti_type] ?? NOTI_ICONS.default;
              const route = resolveRoute(n);
              const isLast = idx === notifications.length - 1;
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    display: "flex", gap: 14, alignItems: "flex-start",
                    padding: "14px 18px",
                    borderBottom: isLast ? "none" : "1px solid var(--border-light)",
                    background: n.is_read ? "transparent" : "var(--brand-light)",
                    cursor: route ? "pointer" : "default",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (route) e.currentTarget.style.background = n.is_read ? "var(--surface-hover)" : "#dbeafe"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? "transparent" : "var(--brand-light)"; }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: meta.bg, color: meta.color,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>
                    <i className={`pi ${meta.icon}`} />
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: n.is_read ? 500 : 700, color: "var(--text-primary)", lineHeight: 1.4 }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 3, lineHeight: 1.5 }}>
                      {n.message}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 5 }}>
                      {formatRelativeTime(n.created_at)}
                    </div>
                  </div>

                  {/* Unread dot */}
                  {!n.is_read && (
                    <div style={{
                      width: 10, height: 10, borderRadius: "50%",
                      background: "#ef4444", flexShrink: 0, marginTop: 5,
                      boxShadow: "0 0 0 2px white",
                    }} />
                  )}

                  {/* Arrow if navigable */}
                  {route && (
                    <i className="pi pi-chevron-right" style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0, alignSelf: "center" }} />
                  )}
                </div>
              );
            })}

            {/* Load more states */}
            {loadingMore && (
              <div style={{ padding: "16px", textAlign: "center" }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, color: "var(--brand-primary)" }} />
              </div>
            )}
            {!hasMore && notifications.length > 0 && (
              <div style={{ padding: "14px", textAlign: "center", fontSize: 12, color: "var(--text-muted)" }}>
                You're all caught up
              </div>
            )}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
